import { useEffect, useState } from "react";
import UserFilters from "./UserFilters.jsx";
import UserTable from "./UserTable.jsx";
import { getUsersList } from "../../api/users.js";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Heading from "../../components/Heading.jsx";
import { useGetUserByUserType } from "../../queries/user.js";
import Loader from "../../components/Common/Loader.jsx";

function UserList() {

    const [filteredUsers, setFilteredUsers] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const genderFilters = [
        { value: 'male', placeholder: 'Male' },
        { value: 'female', placeholder: 'Female' },
        { value: 'other', placeholder: 'Other' }
    ];
    const ageGroupFilters = [
        { value: 'Gen Alpha', placeholder: 'Gen Alpha (born 2010 - 2024)' },
        { value: 'Gen Z', placeholder: 'Gen Z (born 1997 - 2010)' },
        { value: 'Millennials', placeholder: 'Millennials (born 1981 – 1996)' },
        { value: 'Generation X', placeholder: 'Generation X (born 1965-1980)' },
        { value: 'Baby Boomers', placeholder: 'Baby Boomers (born 1946-1964)' },
    ];
    const userActiveFilters = [
        { value: true, placeholder: 'Active' },
        { value: false, placeholder: 'Inactive' },
    ];
      
    const userTypeFilters = [
        { value: 'user', placeholder: 'User' },
        { value: 'ambassador', placeholder: 'Ambassador' },
        { value: 'NGO', placeholder: 'NGO' },
        { value: 'Company', placeholder: 'Company' },
    ];
    
    const [filters, setFilters] = useState([
        { label: 'Full Name', placeholder: 'Enter Full Name', name: 'name', value: '', type: 'text' },
        { label: 'User Name', placeholder: 'Enter User Name', name: 'username', value: '', type: 'text' },
        { label: 'Gender', placeholder: 'Select Gender', data: genderFilters, name: 'gender', value: '', type: 'select' },
        { label: 'Age Group', placeholder: 'Select Age Group', data: ageGroupFilters, name: 'ageGroup', value: '', type: 'select' },
        { label: 'City', placeholder: 'Enter City', name: 'city', value: '', type: 'text' },
        { label: 'Email', placeholder: 'Enter Email', name: 'email', value: '', type: 'email' },
        { label: 'Status', placeholder: 'Select Status', data: userActiveFilters, name: 'active', value: '', type: 'select' },
        { label: 'Referred', placeholder: 'Referred', data: [{value: true, placeholder: 'Yes'}, {value: false, placeholder: 'No'}], name: 'isReferred', value: '', type: 'select' },
        { label: 'Referral Code', placeholder: 'Enter Referral Code', name: 'referralCode', value: '', type: 'text' },
    ]);

    const filterUsers = async (page = 1) => {
        // Build a payload from active filters and call server-side filtering
        const payload = { page, limit: 50 };
        filters.forEach(f => {
            if (f.value !== null && f.value !== undefined && String(f.value).trim() !== '') {
                payload[f.name] = f.value;
            }
        });

        console.log('🔍 filterUsers - Applying filters:', payload);

        try {
            const result = await getUsersList(toast, 'user', payload);
            console.log('✅ filterUsers - Result:', result);

            if (result) {
                setFilteredUsers(result.users || []);
                setPagination(result.pagination || null);
                setCurrentPage(page);
            } else {
                console.warn('⚠️ filterUsers - No result returned from getUsersList');
                setFilteredUsers([]);
                setPagination(null);
            }
        } catch (err) {
            console.error('❌ filterUsers - Error:', err);
            toast.error('Failed to apply filters.');
            setFilteredUsers([]);
            setPagination(null);
        }
    };

    const clearFilters = async () => {
        const resetFilters = filters.map(filter => ({ ...filter, value: '' }));
        setFilters(resetFilters);
        setCurrentPage(1);
        // Fetch unfiltered list from server
        try {
            const result = await getUsersList(toast, 'user', { page: 1, limit: 50 });
            if (result) {
                setFilteredUsers(result.users || []);
                setPagination(result.pagination || null);
            }
        } catch (err) {
            console.error('Failed to fetch users', err);
            toast.error('Failed to fetch users!');
        }
    }

    const { data: users, isPending, error } = useGetUserByUserType({ toast, userType: 'user' });

    useEffect(() => {
        if(users) {
            setFilteredUsers(users.users || users || []);
            setPagination(users.pagination || null);
        }
    }, [users]);
    
    if(isPending) {
        return(
            <div className="flex justify-center items-center h-screen">
                <Loader />
            </div>
        );
    }
    
    if(error) {
        toast.error('Failed to fetch users!');
    }
    
    return (
        <main className="flex flex-col text-sm font-semibold text-gray-900 rounded-none ">
            <Heading
                text={"App Users List"}
            />
            <UserFilters
                filters={filters}
                setFilters={setFilters}
                filterUsers={filterUsers}
                clearFilters={clearFilters}
            />
            <section className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                    <h1 className="self-start text-base font-semibold">User Data List</h1>
                    <UserTable users={filteredUsers} toast={toast} />

                    {/* Pagination Controls */}
                    {pagination && pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between py-4 px-6 border-t border-gray-200">
                            <div className="text-sm text-gray-700">
                                Showing page <span className="font-semibold">{pagination.currentPage}</span> of{' '}
                                <span className="font-semibold">{pagination.totalPages}</span> ({pagination.totalCount} total users)
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => filterUsers(currentPage - 1)}
                                    disabled={!pagination.hasPrevPage}
                                    className={`px-4 py-2 text-sm font-medium rounded-md ${
                                        pagination.hasPrevPage
                                            ? 'bg-primary text-white hover:bg-green-600'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    Previous
                                </button>
                                <span className="px-4 py-2 text-sm font-medium">
                                    Page {pagination.currentPage} / {pagination.totalPages}
                                </span>
                                <button
                                    onClick={() => filterUsers(currentPage + 1)}
                                    disabled={!pagination.hasNextPage}
                                    className={`px-4 py-2 text-sm font-medium rounded-md ${
                                        pagination.hasNextPage
                                            ? 'bg-primary text-white hover:bg-green-600'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>
            <ToastContainer
                position = "top-right"
                autoClose={1000}
            />
        </main>
    );
}

export default UserList;